import { CompanyId } from "@custom-types/base-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CompanySettingsResponse,
  CompanySettingsScope,
  UserSettingsState,
} from "@store/settings/settings-slice-types";
import {
  fetchCompanySettings,
  fetchUserSettings,
  updateCompanySetting,
  updateUserSetting,
} from "@store/settings/settings-thunks";

// Note: Device settings are not defined yet in the library

export interface SettingsState {
  user: UserSettingsState;
  company: CompanySettingsScope;
  // device: DeviceSettings;
}

export const initialState: SettingsState = {
  user: {
    requestState: "idle",
    data: {},
  },
  company: {},
  // device: {},
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setCompanySetting: (
      state,
      action: PayloadAction<{
        companyId: CompanyId;
        body: CompanySettingsResponse;
      }>
    ) => {
      const { companyId, body } = action.payload;
      if (!state.company[companyId]) {
        state.company[companyId] = [];
      }
      // Search by identifier and push new setting or replace if exists.
      const existingSetting = state.company[companyId].findIndex(
        (item) => item.identifier === body.identifier
      );
      if (existingSetting === -1) {
        state.company[companyId].push(body);
      } else {
        state.company[companyId][existingSetting] = body;
      }
    },
    resetSettingsState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUserSettings.pending, (state) => {
        state.user.requestState = "fetching";
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.user.data[action.meta.arg.userId] = action.payload;
        state.user.requestState = "idle";
      })
      .addCase(fetchUserSettings.rejected, (state) => {
        state.user.requestState = "idle";
      })
      .addCase(updateUserSetting.pending, (state) => {
        state.user.requestState = "updating";
      })
      .addCase(updateUserSetting.fulfilled, (state, action) => {
        const { userId, setting } = action.payload;
        if (!state.user.data[userId]) {
          state.user.data[userId] = {};
        }
        // Update the specific setting with the new value and modifiedAt
        state.user.data[userId] = {
          ...state.user.data[userId],
          [setting.identifier]: setting,
        };
        state.user.requestState = "idle";
      })
      .addCase(updateUserSetting.rejected, (state) => {
        state.user.requestState = "idle";
      })
      .addCase(fetchCompanySettings.fulfilled, (state, action) => {
        const { companyId } = action.meta.arg;
        state.company[companyId] = Object.values(action.payload);
      })
      .addCase(updateCompanySetting.fulfilled, (state, action) => {
        const { companyId, setting } = action.payload;

        if (!state.company[companyId]) {
          state.company[companyId] = [];
        }

        const existingSettingIndex = state.company[companyId].findIndex(
          (item) => item.identifier === setting.identifier
        );

        if (existingSettingIndex === -1) {
          state.company[companyId].push(setting);
          return;
        }

        state.company[companyId][existingSettingIndex] = setting;
      });
  },
});

export const {
  setCompanySetting,
  // setDeviceSetting,
  resetSettingsState,
} = settingsSlice.actions;

export const settingsReducer = settingsSlice.reducer;
